/**
 * `human_handover` event — the first notification consumer.
 *
 * Emitted when the AI escalates a conversation to a human. The payload carries
 * the rendering source (reason, optional summary, tags, contact) plus the
 * deep-link anchor so a renderer can link back to the exact item. CRM lead
 * creation is NOT part of this event — it stays a handover-local action.
 *
 * Voice handover is out of scope; `scopeType` covers email + message agents.
 */

import type { TenantEventDefinition } from './definition'

export const HUMAN_HANDOVER_EVENT_TYPE = 'human_handover' as const

export type HumanHandoverScopeType = 'email_agent' | 'message_agent'

export interface HumanHandoverPayload {
  agentId: string
  scopeType: HumanHandoverScopeType
  /** Display name of the agent that escalated, for "where did this fire" context. */
  agentName?: string
  /** AI-provided reason for escalation. */
  reason: string
  /** Optional short TL;DR of the conversation. */
  summary?: string
  tags: string[]
  contact: { name?: string; email?: string }
  /** Deep-link anchor — email agent: numeric DB message id. */
  emailMessageId?: number
  /** Deep-link anchor — message agent: conversation id. */
  conversationId?: string
  /** Message agent: `message_messages.id` of the triggering inbound message. */
  inboundMessageId?: string
  /** CRM lead the escalation created or moved. Notification-only — never returned to the LLM. */
  crmLeadUrl?: string
}

function isHumanHandoverPayload(payload: unknown): payload is HumanHandoverPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.agentId === 'string' &&
    (p.scopeType === 'email_agent' || p.scopeType === 'message_agent') &&
    typeof p.reason === 'string' &&
    Array.isArray(p.tags) &&
    typeof p.contact === 'object' &&
    p.contact !== null
  )
}

export const humanHandoverEventDefinition: TenantEventDefinition<HumanHandoverPayload> = {
  eventType: HUMAN_HANDOVER_EVENT_TYPE,
  supportedChannelTypes: ['email', 'telegram'],
  validatePayload: isHumanHandoverPayload,
  tenant: {
    label: 'Human handover',
    description: 'An email or message agent handed a conversation to a person.',
    recipients: 'agent_settings',
    mandatory: false,
  },
}
